import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import NotificationBell from "../notifications/NotificationBell";
import LocationPicker from '../multiLocation/LocationPicker';
import BackButton from "../common/BackButton";
import "../new-service/CatalogueTopNav.css";
import "./NewForm.css";

// Question type icon components
const QuestionTypeIcons = {
  short_answer: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="14" y2="18" />
    </svg>
  ),
  long_answer: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="14" y2="18" />
    </svg>
  ),
  multiple_choice: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="6" cy="12" r="3" />
      <line x1="13" y1="12" x2="21" y2="12" strokeLinecap="round" />
      <circle cx="6" cy="5" r="3" opacity="0.4" />
      <line x1="13" y1="5" x2="21" y2="5" strokeLinecap="round" opacity="0.4" />
    </svg>
  ),
  checkbox: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  dropdown: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="3" />
      <polyline points="15 11 12 14 9 11" />
    </svg>
  ),
  yes_no: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M7 13l3 3 7-7" />
      <line x1="17" y1="7" x2="7" y2="17" opacity="0.35" />
    </svg>
  ),
};

// Question type options
const QUESTION_TYPES = [
  { value: 'short_answer', label: 'Short Answer', icon: QuestionTypeIcons.short_answer },
  { value: 'long_answer', label: 'Long Answer', icon: QuestionTypeIcons.long_answer },
  { value: 'multiple_choice', label: 'Multiple Choice', icon: QuestionTypeIcons.multiple_choice },
  { value: 'checkbox', label: 'Checkbox', icon: QuestionTypeIcons.checkbox },
  { value: 'dropdown', label: 'Drop down', icon: QuestionTypeIcons.dropdown },
  { value: 'yes_no', label: 'Yes or No', icon: QuestionTypeIcons.yes_no }
];

// Default client details fields
const DEFAULT_CLIENT_FIELDS = [
  { id: 'firstName', label: 'First Name', enabled: true, required: true },
  { id: 'lastName', label: 'Last Name', enabled: true, required: true },
  { id: 'email', label: 'Email', enabled: true, required: true },
  { id: 'mobileNumber', label: 'Mobile Number', enabled: true, required: true },
  { id: 'birthday', label: 'Birthday', enabled: false, required: false },
  { id: 'address', label: 'Address', enabled: false, required: false }
];

/**
 * NewForm - Component for creating new client forms
 */
const DEFAULT_SECTIONS = [
  {
    id: 'section_1',
    type: 'client_details',
    title: 'Client Details',
    description: '',
    fields: [...DEFAULT_CLIENT_FIELDS],
    questions: []
  }
];

const NewForm = (props) => {
  const incomingState = props.location?.state;
  const [activeTab, setActiveTab] = useState('edit'); // 'edit' or 'preview'
  const [sections, setSections] = useState(incomingState?.formData || DEFAULT_SECTIONS);
  const [activeSection, setActiveSection] = useState('section_1');
  const [isEditing, setIsEditing] = useState(incomingState?.isEditing || false);
  const [editFormId, setEditFormId] = useState(incomingState?.formId || null);
  const [editFormSettings, setEditFormSettings] = useState(incomingState?.formSettings || null);
  const [openMenuSectionId, setOpenMenuSectionId] = useState(null);
  const [openTypeDropdown, setOpenTypeDropdown] = useState(null); // Track which question's type dropdown is open
  const [focusedQuestionId, setFocusedQuestionId] = useState(null); // Track expanded question card
  const [autoFocusQuestionId, setAutoFocusQuestionId] = useState(null); // Auto-focus input on new question

  const [dragState, setDragState] = useState({ type: null, sourceId: null, sourceSectionId: null });
  const [dragOverId, setDragOverId] = useState(null);

  const menuRef = useRef(null);
  const questionRefs = useRef({});
  const sectionRefs = useRef({});

  // Auto-focus the question input when a new question is created via Tab
  useEffect(() => {
    if (autoFocusQuestionId) {
      const el = questionRefs.current[autoFocusQuestionId];
      if (el) {
        const input = el.querySelector('.question-text-input input');
        if (input) {
          setTimeout(() => input.focus(), 50);
        }
      }
      setAutoFocusQuestionId(null);
    }
  }, [autoFocusQuestionId]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuSectionId && !event.target.closest('.section-menu-container')) {
        setOpenMenuSectionId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuSectionId]);

  // Navigate back
  const goBack = () => {
    props.history.push('/forms');
  };

  // Navigate to next step (Form Settings)
  const goNext = () => {
    // Navigate to form settings with form data
    props.history.push({
      pathname: '/forms/settings',
      state: {
        formData: sections,
        isEditing: isEditing,
        formId: editFormId,
        formSettings: editFormSettings,
        formName: props.location?.state?.formName || '',
        heroImage: props.location?.state?.heroImage || '',
        isTemplate: props.location?.state?.isTemplate || false
      }
    });
  };

  // Add a new section
  const addSection = () => {
    const newSectionId = `section_${sections.length + 1}`;
    const newSection = {
      id: newSectionId,
      type: 'custom',
      title: '',
      description: '',
      fields: [],
      questions: []
    };
    setSections([...sections, newSection]);
    setActiveSection(newSectionId);
  };

  // Update section title
  const updateSectionTitle = (sectionId, title) => {
    setSections(sections.map(section => 
      section.id === sectionId ? { ...section, title } : section
    ));
  };

  // Update section description
  const updateSectionDescription = (sectionId, description) => {
    setSections(sections.map(section => 
      section.id === sectionId ? { ...section, description } : section
    ));
  };

  // Toggle client detail field
  const toggleClientField = (sectionId, fieldId) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          fields: section.fields.map(field =>
            field.id === fieldId ? { ...field, enabled: !field.enabled } : field
          )
        };
      }
      return section;
    }));
  };

  // Update client detail field label
  const updateClientFieldLabel = (sectionId, fieldId, newLabel) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          fields: section.fields.map(field =>
            field.id === fieldId ? { ...field, label: newLabel } : field
          )
        };
      }
      return section;
    }));
  };

  // Add a question to a section, optionally after a specific question
  const addQuestion = (sectionId, afterQuestionId) => {
    const questionId = `question_${Date.now()}`;
    const newQuestion = {
      id: questionId,
      type: 'short_answer',
      question: '',
      required: true,
      options: ['Answer 1', 'Answer 2']
    };

    setSections(sections.map(section => {
      if (section.id === sectionId) {
        if (afterQuestionId) {
          const idx = section.questions.findIndex(q => q.id === afterQuestionId);
          const newQuestions = [...section.questions];
          newQuestions.splice(idx + 1, 0, newQuestion);
          return { ...section, questions: newQuestions };
        }
        return {
          ...section,
          questions: [...section.questions, newQuestion]
        };
      }
      return section;
    }));
    setFocusedQuestionId(questionId);
    if (afterQuestionId) {
      setAutoFocusQuestionId(questionId);
    }
  };

  // Scroll to a question or section
  const scrollToElement = (id, type) => {
    const ref = type === 'question' ? questionRefs.current[id] : sectionRefs.current[id];
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (type === 'question') {
      setFocusedQuestionId(id);
    }
  };

  // --- Drag and drop: Sections ---
  const handleSectionDragStart = (e, sectionId) => {
    setDragState({ type: 'section', sourceId: sectionId, sourceSectionId: null });
    e.dataTransfer.effectAllowed = 'move';
    // Use a timeout so the dragging class applies after the browser captures the drag image
    const sectionEl = sectionRefs.current[sectionId];
    setTimeout(() => { if (sectionEl) sectionEl.classList.add('dragging'); }, 0);
  };

  const handleSectionDragEnd = () => {
    const prevId = dragState.sourceId;
    setDragState({ type: null, sourceId: null, sourceSectionId: null });
    setDragOverId(null);
    const sectionEl = sectionRefs.current[prevId];
    if (sectionEl) sectionEl.classList.remove('dragging');
  };

  const handleSectionDragOver = (e, targetSectionId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragState.type === 'section' && dragState.sourceId !== targetSectionId) {
      setDragOverId(targetSectionId);
    }
  };

  const handleSectionDragLeave = (e, targetSectionId) => {
    // Only clear if we actually left the section (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (dragOverId === targetSectionId) setDragOverId(null);
    }
  };

  const handleSectionDrop = (e, targetSectionId) => {
    e.preventDefault();
    setDragOverId(null);
    if (dragState.type !== 'section' || dragState.sourceId === targetSectionId) return;
    const fromIndex = sections.findIndex(s => s.id === dragState.sourceId);
    const toIndex = sections.findIndex(s => s.id === targetSectionId);
    if (fromIndex === -1 || toIndex === -1) return;
    const newSections = [...sections];
    const [moved] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, moved);
    setSections(newSections);
    setDragState({ type: null, sourceId: null, sourceSectionId: null });
  };

  // --- Drag and drop: Questions ---
  const handleQuestionDragStart = (e, questionId, sectionId) => {
    e.stopPropagation();
    setDragState({ type: 'question', sourceId: questionId, sourceSectionId: sectionId });
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.closest('.question-card').classList.add('dragging');
  };

  const handleQuestionDragEnd = (e) => {
    setDragState({ type: null, sourceId: null, sourceSectionId: null });
    const card = e.currentTarget.closest ? e.currentTarget.closest('.question-card') : e.currentTarget;
    if (card) card.classList.remove('dragging');
  };

  const handleQuestionDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleQuestionDrop = (e, targetQuestionId, targetSectionId) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragState.type !== 'question' || dragState.sourceId === targetQuestionId) return;
    const sourceSectionId = dragState.sourceSectionId;
    const sourceSection = sections.find(s => s.id === sourceSectionId);
    const targetSection = sections.find(s => s.id === targetSectionId);
    if (!sourceSection || !targetSection) return;

    const sourceQuestion = sourceSection.questions.find(q => q.id === dragState.sourceId);
    if (!sourceQuestion) return;

    if (sourceSectionId === targetSectionId) {
      // Reorder within same section
      const fromIndex = sourceSection.questions.findIndex(q => q.id === dragState.sourceId);
      const toIndex = sourceSection.questions.findIndex(q => q.id === targetQuestionId);
      const newQuestions = [...sourceSection.questions];
      const [moved] = newQuestions.splice(fromIndex, 1);
      newQuestions.splice(toIndex, 0, moved);
      setSections(sections.map(s => s.id === sourceSectionId ? { ...s, questions: newQuestions } : s));
    } else {
      // Move across sections
      const newSourceQuestions = sourceSection.questions.filter(q => q.id !== dragState.sourceId);
      const toIndex = targetSection.questions.findIndex(q => q.id === targetQuestionId);
      const newTargetQuestions = [...targetSection.questions];
      newTargetQuestions.splice(toIndex, 0, sourceQuestion);
      setSections(sections.map(s => {
        if (s.id === sourceSectionId) return { ...s, questions: newSourceQuestions };
        if (s.id === targetSectionId) return { ...s, questions: newTargetQuestions };
        return s;
      }));
    }
    setDragState({ type: null, sourceId: null, sourceSectionId: null });
  };

  // Update question
  const updateQuestion = (sectionId, questionId, updates) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          questions: section.questions.map(q => 
            q.id === questionId ? { ...q, ...updates } : q
          )
        };
      }
      return section;
    }));
  };

  // Delete question
  const deleteQuestion = (sectionId, questionId) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          questions: section.questions.filter(q => q.id !== questionId)
        };
      }
      return section;
    }));
  };

  // Duplicate question
  const duplicateQuestion = (sectionId, questionId) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        const questionToDuplicate = section.questions.find(q => q.id === questionId);
        if (questionToDuplicate) {
          const newQuestion = {
            ...questionToDuplicate,
            id: `question_${Date.now()}`
          };
          const questionIndex = section.questions.findIndex(q => q.id === questionId);
          const newQuestions = [...section.questions];
          newQuestions.splice(questionIndex + 1, 0, newQuestion);
          return { ...section, questions: newQuestions };
        }
      }
      return section;
    }));
  };

  // Add option to multiple choice/checkbox/dropdown question
  const addOption = (sectionId, questionId) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id === questionId) {
              return {
                ...q,
                options: [...q.options, `Answer ${q.options.length + 1}`]
              };
            }
            return q;
          })
        };
      }
      return section;
    }));
  };

  // Update option
  const updateOption = (sectionId, questionId, optionIndex, value) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id === questionId) {
              const newOptions = [...q.options];
              newOptions[optionIndex] = value;
              return { ...q, options: newOptions };
            }
            return q;
          })
        };
      }
      return section;
    }));
  };

  // Delete option
  const deleteOption = (sectionId, questionId, optionIndex) => {
    setSections(sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          questions: section.questions.map(q => {
            if (q.id === questionId) {
              const newOptions = q.options.filter((_, i) => i !== optionIndex);
              return { ...q, options: newOptions };
            }
            return q;
          })
        };
      }
      return section;
    }));
  };

  // Delete section
  const deleteSection = (sectionId) => {
    if (sections.length > 1) {
      setSections(sections.filter(s => s.id !== sectionId));
      if (activeSection === sectionId) {
        setActiveSection(sections[0].id);
      }
    }
    setOpenMenuSectionId(null);
  };

  // Toggle section menu
  const toggleSectionMenu = (sectionId) => {
    setOpenMenuSectionId(openMenuSectionId === sectionId ? null : sectionId);
  };

  // Render question type icon
  const getQuestionTypeIcon = (type) => {
    const questionType = QUESTION_TYPES.find(t => t.value === type);
    return questionType ? questionType.icon : '═';
  };

  // Render client details section
  const renderClientDetailsSection = (section) => (
    <div className="form-section-content">
      <div className="form-field-group">
        <label className="form-field-label">Section Title</label>
        <input
          type="text"
          className="form-field-input"
          value={section.title}
          onChange={(e) => updateSectionTitle(section.id, e.target.value)}
          placeholder="Section Title"
        />
      </div>
      
      <div className="form-field-group">
        <label className="form-field-label">Section Description</label>
        <textarea
          className="form-field-textarea"
          value={section.description}
          onChange={(e) => updateSectionDescription(section.id, e.target.value)}
          placeholder="Section Description"
          rows={3}
        />
      </div>

      {section.fields.map((field) => (
        <div key={field.id} className={`client-field-row ${!field.enabled ? 'client-field-row--disabled' : ''}`}>
          <span className="client-field-label">{field.label}</span>
          <label className="client-field-toggle-switch">
            <input
              type="checkbox"
              checked={field.enabled}
              onChange={() => toggleClientField(section.id, field.id)}
            />
            <span className="client-field-toggle-slider"></span>
          </label>
        </div>
      ))}
    </div>
  );

  // Render custom section
  const renderCustomSection = (section) => (
    <div className="form-section-content">
      <div className="form-field-group">
        <label className="form-field-label">Section Title</label>
        <input
          type="text"
          className="form-field-input"
          value={section.title}
          onChange={(e) => updateSectionTitle(section.id, e.target.value)}
          placeholder="Section Title"
        />
      </div>
      
      <div className="form-field-group">
        <label className="form-field-label">Section Description</label>
        <textarea
          className="form-field-textarea"
          value={section.description}
          onChange={(e) => updateSectionDescription(section.id, e.target.value)}
          placeholder="Section Description"
          rows={3}
        />
      </div>

      {/* Questions */}
      {section.questions.map((question) => {
        const isExpanded = focusedQuestionId === question.id;
        return (
          <div
            key={question.id}
            ref={(el) => { questionRefs.current[question.id] = el; }}
            className={`question-card ${isExpanded ? 'expanded' : 'collapsed'}`}
            onDragOver={handleQuestionDragOver}
            onDrop={(e) => handleQuestionDrop(e, question.id, section.id)}
          >
            {/* Collapsed summary row */}
            {!isExpanded && (
              <div
                className="question-summary-row"
                tabIndex={0}
                role="button"
                onClick={() => setFocusedQuestionId(question.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setFocusedQuestionId(question.id);
                  }
                }}
              >
                <span
                  className="question-summary-grip"
                  draggable
                  tabIndex={-1}
                  onDragStart={(e) => handleQuestionDragStart(e, question.id, section.id)}
                  onDragEnd={handleQuestionDragEnd}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="grip-dots"></span>
                </span>
                <span className="question-summary-icon">{getQuestionTypeIcon(question.type)}</span>
                <span className="question-summary-text">
                  {question.question || 'Untitled question'}
                </span>
                {question.required && (
                  <span className="question-summary-required">Required</span>
                )}
                <span className="question-summary-type">
                  {QUESTION_TYPES.find(t => t.value === question.type)?.label}
                </span>
              </div>
            )}

            {/* Expanded editing UI */}
            {isExpanded && (
              <div
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    setFocusedQuestionId(null);
                  }
                }}
              >
                <div className="question-card-header">
                  <span
                    className="question-drag-handle"
                    draggable
                    tabIndex={-1}
                    onDragStart={(e) => handleQuestionDragStart(e, question.id, section.id)}
                    onDragEnd={handleQuestionDragEnd}
                  >
                    <span className="grip-dots"></span>
                  </span>
                  <button
                    className="question-delete-btn"
                    tabIndex={-1}
                    onClick={() => deleteQuestion(section.id, question.id)}
                  >
                    ✕
                  </button>
                </div>

                <div className="question-card-body">
                  <div className="question-row">
                    <div className="question-type-select">
                      <label className="form-field-label">Question type</label>
                      <div className="question-type-dropdown-wrapper">
                        <button
                          className="question-type-trigger"
                          onClick={() => setOpenTypeDropdown(openTypeDropdown === question.id ? null : question.id)}
                        >
                          <span className="question-type-icon">{getQuestionTypeIcon(question.type)}</span>
                          <span>{QUESTION_TYPES.find(t => t.value === question.type)?.label}</span>
                          <svg
                            className={`question-type-chevron ${openTypeDropdown === question.id ? 'open' : ''}`}
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </button>
                        {openTypeDropdown === question.id && (
                          <>
                            <div
                              className="question-type-backdrop"
                              onClick={() => setOpenTypeDropdown(null)}
                            />
                            <div className="question-type-menu">
                              {QUESTION_TYPES.map(type => (
                                <button
                                  key={type.value}
                                  className={`question-type-option ${question.type === type.value ? 'active' : ''}`}
                                  onClick={() => {
                                    updateQuestion(section.id, question.id, { type: type.value });
                                    setOpenTypeDropdown(null);
                                  }}
                                >
                                  <span className="question-type-option-icon">{type.icon}</span>
                                  <span>{type.label}</span>
                                  {question.type === type.value && (
                                    <svg className="question-type-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="question-text-input">
                      <label className="form-field-label">Question</label>
                      <input
                        type="text"
                        className="form-field-input"
                        value={question.question}
                        onChange={(e) => updateQuestion(section.id, question.id, { question: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Tab' && !e.shiftKey) {
                            e.preventDefault();
                            addQuestion(section.id, question.id);
                          }
                        }}
                        placeholder="Have you ever had hair colour before?"
                      />
                    </div>
                  </div>

                  {/* Options for multiple choice, checkbox, dropdown */}
                  {['multiple_choice', 'checkbox', 'dropdown'].includes(question.type) && (
                    <div className="question-options">
                      {question.options.map((option, index) => (
                        <div key={index} className="option-row">
                          <input
                            type="text"
                            className="option-input"
                            value={option}
                            onChange={(e) => updateOption(section.id, question.id, index, e.target.value)}
                            placeholder={`Answer ${index + 1}`}
                          />
                          <button
                            className="option-delete-btn"
                            tabIndex={-1}
                            onClick={() => deleteOption(section.id, question.id, index)}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      <button
                        className="add-option-btn"
                        onClick={() => addOption(section.id, question.id)}
                      >
                        Add Answer
                      </button>
                    </div>
                  )}

                  <div className="question-footer">
                    <button
                      className="duplicate-btn"
                      tabIndex={-1}
                      onClick={() => duplicateQuestion(section.id, question.id)}
                      title="Duplicate question"
                    >
                      ⧉
                    </button>
                    <div className="required-toggle">
                      <span>Required</span>
                      <label className="form-toggle-switch">
                        <input
                          type="checkbox"
                          checked={question.required}
                          onChange={(e) => updateQuestion(section.id, question.id, { required: e.target.checked })}
                        />
                        <span className="form-toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Add Question Button */}
      <button className="add-question-btn" onClick={() => addQuestion(section.id)}>
        <span className="add-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </span>
        Add Question
      </button>
    </div>
  );

  return (
    <div className="h_n catalogue-no-header">
      <div className="body_container cm_bg_white">

        <div className="catalogue-page-wrapper">
          {/* Focused flow header — uses shared catalogue-top-nav for sticky behaviour */}
          <div className="catalogue-top-nav">
            <div className="catalogue-top-nav-container">
              <div className="catalogue-top-nav-content form-flow-header-content">
                <div className="form-flow-header-left">
                  <BackButton onClick={goBack} ariaLabel="Back" />
                  <h1 className="form-flow-header-title">{isEditing ? 'Edit Form' : 'New Form'}</h1>
                </div>
                <div className="form-flow-header-right">
                  <div className="form-tab-toggle">
                    <button
                      className={`tab-btn ${activeTab === 'edit' ? 'active' : ''}`}
                      onClick={() => setActiveTab('edit')}
                    >
                      Edit
                    </button>
                    <button
                      className={`tab-btn ${activeTab === 'preview' ? 'active' : ''}`}
                      onClick={() => setActiveTab('preview')}
                    >
                      Preview
                    </button>
                  </div>
                  <LocationPicker />
                  <NotificationBell />
                </div>
              </div>
            </div>
          </div>

          <div className="new-form-wrapper">

          {/* Outline Sidebar */}
          {activeTab === 'edit' && (
            <div className="form-outline-sidebar">
              <div className="sidebar-header">Outline</div>
              <div className="sidebar-items">
                {sections.map((section, sIndex) => (
                  <div key={section.id} className="sidebar-section-group">
                    <button
                      className={`sidebar-section-item ${section.type === 'client_details' ? 'client-details' : 'custom'}`}
                      onClick={() => scrollToElement(section.id, 'section')}
                    >
                      <span className="sidebar-section-number">{sIndex + 1}</span>
                      <span className="sidebar-section-label">
                        {section.title || (section.type === 'client_details' ? 'Client Details' : 'Untitled Section')}
                      </span>
                    </button>
                    {section.questions.map((q, qIndex) => (
                      <button
                        key={q.id}
                        className={`sidebar-question-item ${focusedQuestionId === q.id ? 'active' : ''}`}
                        onClick={() => scrollToElement(q.id, 'question')}
                      >
                        <span className="sidebar-question-number">{sIndex + 1}.{qIndex + 1}</span>
                        <span className="sidebar-question-label">
                          {q.question || 'Untitled question'}
                        </span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={`new-form-container ${activeTab === 'edit' ? 'with-sidebar' : ''}`}>
            {activeTab === 'edit' ? (
              <>
                {/* Sections */}
                {sections.map((section, index) => (
                  <div
                    key={section.id}
                    ref={(el) => { sectionRefs.current[section.id] = el; }}
                    className={`form-section${dragOverId === section.id ? ' drag-over' : ''}${dragState.sourceId === section.id ? ' dragging' : ''}`}
                    onDragOver={(e) => handleSectionDragOver(e, section.id)}
                    onDragLeave={(e) => handleSectionDragLeave(e, section.id)}
                    onDrop={(e) => handleSectionDrop(e, section.id)}
                  >
                    <div className="form-section-header">
                      <span
                        className="section-drag-handle"
                        draggable
                        tabIndex={-1}
                        onDragStart={(e) => handleSectionDragStart(e, section.id)}
                        onDragEnd={handleSectionDragEnd}
                      >
                        <span className="grip-dots"></span>
                      </span>
                      <span 
                        className={`section-tab ${section.type === 'client_details' ? 'client-details' : 'custom'}`}
                      >
                        Section {index + 1} - {section.type === 'client_details' ? 'Client Details' : 'Custom'}
                      </span>
                      {section.type !== 'client_details' && (
                        <div className="section-menu-container">
                          <button
                            className="section-menu-btn"
                            tabIndex={-1}
                            onClick={() => toggleSectionMenu(section.id)}
                          >
                            ⋮
                          </button>
                          {openMenuSectionId === section.id && (
                            <div className="section-menu-dropdown">
                              <button
                                className="section-menu-item delete"
                                onClick={() => deleteSection(section.id)}
                              >
                                Delete Category
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {section.type === 'client_details' 
                      ? renderClientDetailsSection(section)
                      : renderCustomSection(section)
                    }
                  </div>
                ))}

                {/* Section Navigation */}
                <div className="section-navigation">
                  <button className="section-nav-btn add-section" onClick={addSection}>
                    Add Section
                  </button>
                </div>
              </>
            ) : (
              /* Preview Mode */
              <div className="form-preview">
                {sections.map((section, index) => (
                  <div key={section.id} className="preview-section">
                    <div className="preview-section-header">
                      <span className={`section-tab ${section.type === 'client_details' ? 'client-details' : 'custom'}`}>
                        Section {index + 1} - {section.type === 'client_details' ? 'Client Details' : 'Custom'}
                      </span>
                    </div>
                    
                    <div className="preview-section-content">
                      {/* Section Title & Description */}
                      <h2 className="preview-section-title">
                        {section.title || 'Section Title'}
                      </h2>
                      <p className="preview-section-description">
                        {section.description || 'Section Description'}
                      </p>

                      {/* Client Details Fields */}
                      {section.type === 'client_details' && section.fields.filter(f => f.enabled).map((field) => (
                        <div key={field.id} className="preview-field">
                          <label className="preview-field-label">{field.label}</label>
                          {field.id === 'address' ? (
                            <textarea
                              className="preview-field-textarea"
                              placeholder=""
                              rows={3}
                              readOnly
                            />
                          ) : (
                            <input
                              type={field.id === 'email' ? 'email' : field.id === 'birthday' ? 'date' : 'text'}
                              className="preview-field-input"
                              placeholder=""
                              readOnly
                            />
                          )}
                        </div>
                      ))}

                      {/* Custom Section Questions */}
                      {section.type === 'custom' && section.questions.map((question) => (
                        <div key={question.id} className="preview-question">
                          <label className="preview-question-label">
                            {question.question || 'Question?'}
                          </label>
                          
                          {/* Short Answer */}
                          {question.type === 'short_answer' && (
                            <input
                              type="text"
                              className="preview-field-input"
                              placeholder=""
                              readOnly
                            />
                          )}

                          {/* Long Answer */}
                          {question.type === 'long_answer' && (
                            <textarea
                              className="preview-field-textarea"
                              placeholder=""
                              rows={4}
                              readOnly
                            />
                          )}

                          {/* Multiple Choice */}
                          {question.type === 'multiple_choice' && (
                            <div className="preview-options">
                              {question.options.map((option, idx) => (
                                <label key={idx} className="preview-radio-option">
                                  <input
                                    type="radio"
                                    name={`preview_${question.id}`}
                                    className="preview-radio"
                                  />
                                  <span className="preview-radio-custom"></span>
                                  <span className="preview-option-text">{option}</span>
                                </label>
                              ))}
                            </div>
                          )}

                          {/* Checkbox */}
                          {question.type === 'checkbox' && (
                            <div className="preview-options">
                              {question.options.map((option, idx) => (
                                <label key={idx} className="preview-checkbox-option">
                                  <input
                                    type="checkbox"
                                    className="preview-checkbox"
                                  />
                                  <span className="preview-checkbox-custom"></span>
                                  <span className="preview-option-text">{option}</span>
                                </label>
                              ))}
                            </div>
                          )}

                          {/* Dropdown */}
                          {question.type === 'dropdown' && (
                            <div className="preview-dropdown-wrapper">
                              <select className="preview-dropdown">
                                {question.options.map((option, idx) => (
                                  <option key={idx} value={option}>{option}</option>
                                ))}
                              </select>
                              <span className="preview-dropdown-arrow">▼</span>
                            </div>
                          )}

                          {/* Yes or No */}
                          {question.type === 'yes_no' && (
                            <div className="preview-yes-no">
                              <button className="preview-yes-btn active">Yes</button>
                              <button className="preview-no-btn">No</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

          {/* Sticky Bottom Bar with Step Indicator */}
          <div className="form-flow-bottom-bar">
            <button className="form-flow-btn form-flow-btn-back" onClick={goBack}>
              Back
            </button>
            <div className="form-flow-steps">
              <span className="form-flow-step active"></span>
              <span className="form-flow-step"></span>
            </div>
            <button className="form-flow-btn form-flow-btn-next" onClick={goNext}>
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    ...state
  };
}

export default withRouter(connect(mapStateToProps)(NewForm));
